/**
 * Ventana de documento (DESIGN.md §11.7, maqueta m12): hoja centrada de 1.220 px × 93 % del alto, radio 14, sobre un
 * velo `slate-900` al 30 %, con la lista de fondo intacta. Tiene URL propia (`/documento/:id`, decisión del dueño 19-10):
 * la lista navega con `state.fondo` y App dibuja la lista (fondo) y esta ventana encima. Abrir la misma URL sin fondo
 * (pestaña nueva, Ctrl + clic, recarga sin historia) muestra la página `DocumentDetail`.
 *
 * - Nace de la fila pulsada: escala 0,98 → 1 y opacidad desde el centro de la fila, resorte sin rebote de 0,25 s;
 *   con movimiento reducido, solo fundido.
 * - ↑ ↓ recorren la lista de la que vino; las citas se abren aquí mismo. Todo con `replace`: cerrar vuelve
 *   siempre a la lista.
 * - Esc o el velo cierran y el foco vuelve a la fila del documento abierto; la lista conserva su posición.
 */
import { CSSProperties, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Location, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import FichaDocumento from './FichaDocumento';
import { useDocumento } from '../../hooks/useDocumento';

export interface EstadoVentana {
  fondo: Location;
  /** Ids de la lista, en orden, para ↑ ↓. */
  lista?: string[];
  /** Búsqueda de la lista: se lleva al buscador del texto. */
  consulta?: string;
}

/** Opciones de `navigate` para abrir un documento en la ventana desde una lista. */
export const abrirEnVentana = (fondo: Location, lista: string[], consulta?: string) => ({ state: { fondo, lista, consulta } as EstadoVentana });

/** «#p-6» → 6; cualquier otro hash, nada. */
export const parrafoDeHash = (hash: string): number | null => {
  const m = /^#p-(\d+)$/.exec(hash || '');
  return m ? Number(m[1]) : null;
};

const enfocarFila = (id: string) => {
  const fila = document.querySelector<HTMLElement>(`[data-fila="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id}"]`);
  fila?.focus();
};

export default function VentanaDocumento() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const estado = useMemo(() => (location.state || {}) as EstadoVentana, [location.state]);
  const lista = useMemo(() => estado.lista || [], [estado.lista]);
  const reducido = useReducedMotion();
  const hoja = useRef<HTMLDivElement>(null);
  const datos = useDocumento(id);

  // Origen de la animación: el centro de la fila pulsada, relativo a la hoja.
  const [origen] = useState(() => {
    const fila = document.querySelector<HTMLElement>(`[data-fila="${id}"]`)?.getBoundingClientRect();
    if (!fila || typeof window === 'undefined') return '50% 50%';
    const ancho = Math.min(1220, window.innerWidth - 32);
    const izq = (window.innerWidth - ancho) / 2;
    const arriba = window.innerHeight * 0.035;
    return `${Math.round(fila.left + fila.width / 2 - izq)}px ${Math.round(fila.top + fila.height / 2 - arriba)}px`;
  });

  // Foco: entra a la hoja al abrir; al cerrar vuelve a la fila del documento que quedó abierto (si se llegó por ↑ ↓)
  // o a la fila de la que se abrió (si se terminó en un relacionado, que no está en la lista).
  const origenFila = useRef(id);
  const ultimo = useRef(id);
  ultimo.current = lista.includes(id) ? id : origenFila.current;
  // Al pasar a otro documento (↑ ↓, relacionado) la cabecera se desmonta mientras carga: el foco vuelve a la hoja.
  useEffect(() => {
    const activo = document.activeElement;
    if (!activo || activo === document.body || !hoja.current?.contains(activo)) hoja.current?.focus();
  }, [id, datos.estado]);
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
      const fila = ultimo.current;
      setTimeout(() => enfocarFila(fila), 0);
    };
  }, []);

  const cerrar = useCallback(() => navigate(-1), [navigate]);
  // Una cita o la modificación se abren aquí mismo, en su párrafo («#p-k») si lo trae.
  const ir = useCallback((otro: string, parrafo?: number | null) => navigate(`/documento/${otro}${parrafo ? `#p-${parrafo}` : ''}`, { replace: true, state: { ...estado, consulta: '' } }), [navigate, estado]);
  const parrafoInicial = parrafoDeHash(location.hash);
  const i = lista.indexOf(id);
  const anterior = i > 0 ? () => navigate(`/documento/${lista[i - 1]}`, { replace: true, state: estado }) : undefined;
  const siguiente = i >= 0 && i < lista.length - 1 ? () => navigate(`/documento/${lista[i + 1]}`, { replace: true, state: estado }) : undefined;

  // Esc cierra (en captura: gana a los paneles de la página de fondo), salvo en un campo con texto, donde lo borra.
  // Tab con el foco fuera de la hoja (p. ej. en <body> tras un cambio de documento) vuelve a la hoja.
  useEffect(() => {
    const tecla = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Tab' && hoja.current && !hoja.current.contains(document.activeElement)) {
        e.preventDefault();
        hoja.current.focus();
        return;
      }
      if (e.key !== 'Escape') return;
      const t = e.target;
      if (t instanceof HTMLInputElement && t.value) return;
      // Una fila de cita con su tarjeta abierta (aria-describedby) cierra primero la tarjeta.
      if (t instanceof HTMLElement && t.hasAttribute('aria-describedby')) return;
      e.stopPropagation();
      cerrar();
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  }, [cerrar]);
  const atrapar = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !hoja.current) return;
    const focos = Array.from(hoja.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'));
    if (!focos.length) return;
    const [primero, ultimoFoco] = [focos[0], focos[focos.length - 1]];
    if (e.shiftKey && (document.activeElement === primero || document.activeElement === hoja.current)) { e.preventDefault(); ultimoFoco.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimoFoco) { e.preventDefault(); primero.focus(); }
  };

  const transicion = reducido ? { duration: 0.15 } : { type: 'spring' as const, bounce: 0, duration: 0.25 };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        className="absolute inset-0 bg-slate-900/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={cerrar}
        aria-hidden="true"
      />
      <motion.div
        ref={hoja}
        role="dialog"
        aria-modal="true"
        aria-labelledby={datos.estado === 'listo' ? 'ventana-titulo' : undefined}
        aria-label={datos.estado === 'listo' ? undefined : 'Documento'}
        tabIndex={-1}
        onKeyDown={atrapar}
        initial={reducido ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
        animate={reducido ? { opacity: 1 } : { opacity: 1, scale: 1 }}
        exit={reducido ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
        transition={transicion}
        style={{ transformOrigin: origen } as CSSProperties}
        className="relative w-full max-w-[1220px] h-[93vh] rounded-[14px] bg-white overflow-hidden focus:outline-none border border-slate-200 shadow-[0_12px_32px_rgba(15,23,42,.12)]"
      >
        <FichaDocumento
          id={id}
          datos={datos}
          modo="ventana"
          idTitulo="ventana-titulo"
          consulta={estado.consulta}
          parrafoInicial={parrafoInicial}
          onCerrar={cerrar}
          onAnterior={anterior}
          onSiguiente={siguiente}
          abrirOtro={ir}
        />
      </motion.div>
    </div>
  );
}
